import React, { createContext, useContext, useReducer, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { WS_URL } from '../config/api';
import { apiClient, getAccessToken } from '../api/client';

const OCPPContext = createContext();

const initialState = {
  chargers: {},
  connectionStatus: 'disconnected',
  stats: {
    totalChargers: 0,
    connectedChargers: 0,
    activeTransactions: 0,
    totalPower: 0
  },
  loading: false,
  error: null,
  emergencyActions: []
};

const ocppReducer = (state, action) => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload };

    case 'SET_ERROR':
      return { ...state, error: action.payload, loading: false };

    case 'SET_CONNECTION_STATUS':
      return { ...state, connectionStatus: action.payload };

    case 'SET_CHARGERS': {
      const prev = state.chargers || {};
      const chargers = action.payload.reduce((acc, charger) => {
        const existing = prev[charger.id] || {};
        acc[charger.id] = {
          ...existing,
          ...charger,
          display_name: charger.display_name !== undefined ? charger.display_name : existing.display_name,
          notes: charger.notes !== undefined ? charger.notes : existing.notes,
          site_id: charger.site_id !== undefined ? charger.site_id : existing.site_id,
          connection_status: charger.connected ? 'Connected' : 'Disconnected',
          active_transactions: charger.active_transactions || existing.active_transactions || {}
        };
        return acc;
      }, {});

      const stats = calculateStats(chargers);
      return { ...state, chargers, stats, loading: false };
    }

    case 'UPDATE_CHARGER':
      const updatedChargers = {
        ...state.chargers,
        [action.payload.id]: {
          ...state.chargers[action.payload.id],
          ...action.payload,
          connection_status: action.payload.connected
            ? 'Connected'
            : (action.payload.connection_status
              || state.chargers[action.payload.id]?.connection_status
              || 'Disconnected')
        }
      };
      const updatedStats = calculateStats(updatedChargers);
      return { ...state, chargers: updatedChargers, stats: updatedStats };

    case 'ADD_EMERGENCY_ACTION':
      return {
        ...state,
        emergencyActions: [...state.emergencyActions, action.payload]
      };

    case 'CLEAR_ERROR':
      return { ...state, error: null };

    case 'REMOVE_CHARGER': {
      const remaining = { ...state.chargers };
      delete remaining[action.payload];
      return { ...state, chargers: remaining, stats: calculateStats(remaining) };
    }

    default:
      return state;
  }
};

const calculateStats = (chargers) => {
  const chargerArray = Object.values(chargers);
  return {
    totalChargers: chargerArray.length,
    connectedChargers: chargerArray.filter(c => c.connection_status === 'Connected').length,
    activeTransactions: chargerArray.reduce((sum, c) => {
      const txCount = c.active_transactions ? Object.keys(c.active_transactions).length : 0;
      return sum + txCount;
    }, 0),
    totalPower: chargerArray.reduce((sum, c) => {
      const connectors = c.connectors || [];
      const fromConnectors = connectors.reduce((s, conn) => s + (conn.current_power || 0), 0);
      const live = fromConnectors > 0 ? fromConnectors : (c.current_power || 0);
      return sum + live;
    }, 0) / 1000
  };
};

export const OCPPProvider = ({ children }) => {
  const [state, dispatch] = useReducer(ocppReducer, initialState);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const pollIntervalRef = useRef(null);
  const { isAuthenticated } = useAuth();

  // WebSocket connection for real-time updates
  const connectWebSocket = useCallback(() => {
    // Close existing connection before creating a new one
    if (wsRef.current) {
      wsRef.current.onclose = null; // Prevent triggering reconnect
      wsRef.current.close();
      wsRef.current = null;
    }

    // Clear any pending reconnect timeout
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    try {
      const liveToken = getAccessToken();
      const wsUrl = liveToken ? `${WS_URL}/status?token=${encodeURIComponent(liveToken)}` : `${WS_URL}/status`;
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        dispatch({ type: 'SET_CONNECTION_STATUS', payload: 'connected' });
        dispatch({ type: 'CLEAR_ERROR' });
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          switch (data.type) {
            case 'status_update':
              dispatch({ type: 'SET_CHARGERS', payload: data.chargers });
              break;
            case 'charger_update':
              dispatch({ type: 'UPDATE_CHARGER', payload: data.charger });
              break;
            case 'charger_list':
              dispatch({ type: 'SET_CHARGERS', payload: data.chargers });
              break;
            case 'emergency_response':
              dispatch({ type: 'ADD_EMERGENCY_ACTION', payload: data });
              break;
            default:
              break;
          }
        } catch (error) {
          // Silently ignore parse errors
        }
      };

      ws.onclose = () => {
        dispatch({ type: 'SET_CONNECTION_STATUS', payload: 'disconnected' });
        wsRef.current = null;
        // Schedule reconnect, tracked in ref for cleanup
        reconnectTimeoutRef.current = setTimeout(connectWebSocket, 15000);
      };

      ws.onerror = () => {
        // Don't set a persistent error for transient WS issues - the reconnect
        // logic handles recovery. Only update connection status.
        dispatch({ type: 'SET_CONNECTION_STATUS', payload: 'disconnected' });
      };

    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: 'Failed to connect to server' });
    }
  }, []);

  // Fetch chargers from API
  const fetchChargers = useCallback(async () => {
    dispatch({ type: 'SET_LOADING', payload: true });

    try {
      const response = await apiClient.get('/chargers');
      const chargers = response.data.chargers || response.data;
      dispatch({ type: 'SET_CHARGERS', payload: chargers });
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: `Failed to fetch charger data: ${error.message}` });
    }
  }, []);

  // Emergency stop functions
  const emergencyStop = useCallback(async (chargerId, method = 'zero_power') => {
    try {
      let response;
      const chargerIdParam = `charger_id=${encodeURIComponent(chargerId)}`;

      if (method === 'zero_power') {
        response = await apiClient.post(`/emergency/zero-power?${chargerIdParam}&confirm=true`);
      } else if (method === 'stop_transaction') {
        response = await apiClient.post(`/emergency/stop-transaction?${chargerIdParam}&confirm=true`);
      } else if (method === 'reset_soft') {
        response = await apiClient.post(`/emergency/reset?${chargerIdParam}&reset_type=soft&confirm=true`);
      } else if (method === 'reset_hard') {
        response = await apiClient.post(`/emergency/reset?${chargerIdParam}&reset_type=hard&confirm=true`);
      } else {
        throw new Error(`Unknown emergency method: ${method}`);
      }

      dispatch({
        type: 'ADD_EMERGENCY_ACTION',
        payload: {
          id: Date.now(),
          chargerId,
          method,
          timestamp: new Date().toISOString(),
          status: 'success',
          response: response.data
        }
      });

      return response.data;
    } catch (error) {
      const errorAction = {
        id: Date.now(),
        chargerId,
        method,
        timestamp: new Date().toISOString(),
        status: 'error',
        error: error.response?.data?.detail || error.message
      };

      dispatch({ type: 'ADD_EMERGENCY_ACTION', payload: errorAction });
      throw error;
    }
  }, []);

  const emergencyStopAll = useCallback(async (method = 'zero_power') => {
    try {
      const response = await apiClient.post('/emergency/stop-all?confirm=true', { method });

      dispatch({
        type: 'ADD_EMERGENCY_ACTION',
        payload: {
          id: Date.now(),
          chargerId: 'ALL',
          method,
          timestamp: new Date().toISOString(),
          status: 'success',
          response: response.data
        }
      });

      return response.data;
    } catch (error) {
      const errorAction = {
        id: Date.now(),
        chargerId: 'ALL',
        method,
        timestamp: new Date().toISOString(),
        status: 'error',
        error: error.response?.data?.detail || error.message
      };

      dispatch({ type: 'ADD_EMERGENCY_ACTION', payload: errorAction });
      throw error;
    }
  }, []);

  // Charging profile functions
  const setChargingProfile = useCallback(async (chargerId, profile) => {
    const response = await apiClient.post(`/chargers/profile?charger_id=${encodeURIComponent(chargerId)}&confirm=true`, profile);
    return response.data;
  }, []);

  const clearChargingProfile = useCallback(async (chargerId, options = {}) => {
    const params = new URLSearchParams();

    if (options.profileId !== undefined) params.append('profile_id', options.profileId);
    if (options.connectorId !== undefined) params.append('connector_id', options.connectorId);
    if (options.purpose) params.append('purpose', options.purpose);
    if (options.stackLevel !== undefined) params.append('stack_level', options.stackLevel);

    const queryString = params.toString();
    const url = `/chargers/${encodeURIComponent(chargerId)}/charging-profile${queryString ? '?' + queryString : ''}`;

    const response = await apiClient.delete(url);
    return response.data;
  }, []);

  const clearError = useCallback(() => {
    dispatch({ type: 'CLEAR_ERROR' });
  }, []);

  const getCompositeSchedule = useCallback(async (chargerId, connectorId = 0) => {
    try {
      const response = await apiClient.get(`/chargers/${encodeURIComponent(chargerId)}/composite-schedule?connector_id=${connectorId}`);
      return response.data;
    } catch (error) {
      return null;
    }
  }, []);

  // Initialize data and WebSocket connection
  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    fetchChargers();
    connectWebSocket();

    return () => {
      // Clean up WebSocket
      if (wsRef.current) {
        wsRef.current.onclose = null; // Prevent reconnect on intentional close
        wsRef.current.close();
        wsRef.current = null;
      }
      // Clean up reconnect timeout
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [isAuthenticated, fetchChargers, connectWebSocket]);

  const removeCharger = useCallback((chargerId) => {
    dispatch({ type: 'REMOVE_CHARGER', payload: chargerId });
  }, []);

  const value = {
    ...state,
    fetchChargers,
    emergencyStop,
    emergencyStopAll,
    setChargingProfile,
    clearChargingProfile,
    clearError,
    getCompositeSchedule,
    removeCharger,
  };

  return (
    <OCPPContext.Provider value={value}>
      {children}
    </OCPPContext.Provider>
  );
};

export const useOCPP = () => {
  const context = useContext(OCPPContext);
  if (!context) {
    throw new Error('useOCPP must be used within an OCPPProvider');
  }
  return context;
};
